/**
 * Object / record narrowing predicates. Pure-leaf module: NO value imports.
 *
 * TWO runtime predicates over a SINGLE value, not six. There is exactly ONE behavioural axis over a single value here
 * -- whether arrays pass -- and `isPlainObject` is the only body that answers it differently. Every array-INCLUDING member ({@link isRecordValue},
 * {@link asRecordOrNull}, {@link asRecordOrUndefined}, {@link asRecordOrEmpty}) routes through `isRecordValue`'s single
 * body and every array-EXCLUDING member ({@link isEmptyObject}, {@link asPlainObjectOrNull},
 * {@link asPlainObjectOrUndefined}, {@link asPlainObjectOrEmpty}, {@link isPlainObjectArray}) through
 * `isPlainObject`'s, rather than
 * re-spelling `typeof value === 'object' && value !== null`, which this leaf once carried FOUR times. What still
 * separates the members of one axis is presentation only: whether a miss is `false`, `null`, `undefined` or `{}`.
 *
 * THE MISS VALUE IS PART OF THE NAME, and the EMPTY-RECORD pair below is why that convention had to hold under
 * pressure. The repo-wide idiom the pair retires -- `isRecordValue(x) ? x : {}` and `isPlainObject(x) ? x : {}` -- reads
 * as ONE cohort and is TWO, differing only in whether an array reaches the call site as itself or as `{}`. A single
 * `asRecordOrEmpty` adopted at both spellings would have been byte-invisible and behaviour-changing at every
 * array-EXCLUDING site, which is the same failure the two opposite `asRecord` exports produced. One member per
 * predicate, each named for the predicate it mirrors, is what keeps that unspellable.
 *
 * THE COERCER PAIR IS NAMED FOR THE AXIS, not for a domain, and that is load-bearing: {@link asRecordOrNull} admits
 * `[]` and {@link asPlainObjectOrNull} rejects it, both with the identical `Record<string, unknown> | null` signature.
 * A name that says "record" without saying which answer it gives is the shape that produced two exported `asRecord`
 * functions with OPPOSITE array semantics and identical signatures, where importing the wrong one changed array
 * admission with no type error and no test failure.
 *
 * {@link isRecordValue} narrows to `Record<string, unknown>`, NOT to `object`, and that is load-bearing: the guarded
 * branch indexes by key with no cast, so a call site never re-casts what the predicate already proved. A weaker
 * `object`-narrowing twin of this predicate is the shape to reject, not to re-add.
 *
 * {@link isPlainObjectArray} is the leaf's ONE member on a SECOND axis -- the CONTAINER rather than the value -- and it
 * composes `isPlainObject` rather than re-spelling any element test, so the single-value axis above still has exactly
 * one body per answer. It is named for its ELEMENT predicate rather than "record" for the same reason the coercer pair
 * is named for its axis: a bare "record array" would not say whether a NESTED array passes as a member, which is
 * precisely the ambiguity that produced two opposite `asRecord` exports.
 */

// RELATIVE, not `@federa/const/string-guards`: this leaf is inside the root vitest configs' CONFIG-LOAD value graph
// (reached through `tools/testing/worktree-federa-alias.ts`), which evaluates as plain Node ESM before any alias plugin
// exists -- a root-resolved `@federa/*` specifier there walks MAIN's workspace junction and reads ANOTHER checkout's
// source from a worktree. Pinned by `const/__tests__/vitest-workerd-tier.test.ts`; sibling closure members
// (`const/live-schema-paths.ts`, `const/money-change-journal.ts`) spell their value imports the same way.
import { DB_COLUMNS } from './db-columns.ts';
import { finiteNumberMembers, isFiniteNumber } from './number-guards.ts';
import { stringOrNull } from './string-guards.ts';
/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): generic guard homed in const/ so pure utils/ leaves can
 * value-import it without a forbidden cross-utils sibling dependency. ARRAYS EXCLUDED -- a non-null, non-array
 * object narrowed to a string-keyed record. The default choice for "is this a plain data object".
 */
export function isPlainObject(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): {@link isPlainObject} plus a string `id` -- the admission every
 * record-corpus reader takes before it reads a leaf's other members (a standard's source arm, its supersession edges,
 * its updated-by rebind).
 */
export function isIdentifiedRecord(value: unknown): value is Record<string, unknown> & { id: string } {
	return isPlainObject(value) && typeof value.id === 'string';
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): {@link isPlainObject} plus a string `standardId` -- the admission a
 * standard-sheet reader takes before it reads a row's other members (the board census's rows, the readings store's
 * entries).
 */
export function isStandardIdRecord(value: unknown): value is Record<string, unknown> & { standardId: string } {
	return isPlainObject(value) && typeof value.standardId === 'string';
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): {@link isPlainObject} whose every named member is a function -- the
 * admission a flow host or door reads a suite's injected io through, never a cast (iss-82fc87b3). The watchdog host, the
 * control host and the start door each checked their seams by hand or not at all; one body here is what they compose.
 */
export function hasFunctionMembers(value: unknown, names: readonly string[]): value is Record<string, unknown> {
	return isPlainObject(value) && names.every((name) => typeof value[name] === 'function');
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): generic guard homed in const/ so pure utils/ leaves can
 * value-import it without a forbidden cross-utils sibling dependency. An ARRAY whose every member passes
 * {@link isPlainObject} -- the persisted-read narrowing every DO-cache and D1-row reader needs before it may index
 * members by key. An empty array passes, matching `Array.prototype.every`.
 *
 * THE ONE BODY for `Array.isArray(v) && v.every(isPlainObject)`, which the DO storage-value guards
 * (`isStoredRecordArray`, retired onto this body; the leaf was at `utils/durable-objects/storage-value-guards.ts` then
 * and is `const/do-storage-value-guards.ts` now) and `utils/wishlist/is-wishlist-row-array.ts` each spelled. The
 * latter STAYS as a delegating predicate because it narrows to `WishlistItem[]`, a different answer at the call site;
 * the former narrowed to exactly this type and was therefore a re-export in predicate clothing.
 */
export function isPlainObjectArray(value: unknown): value is Record<string, unknown>[] {
	return Array.isArray(value) && value.every(isPlainObject);
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): generic guard homed in const/ so pure utils/ leaves can
 * value-import it without a forbidden cross-utils sibling dependency. ARRAYS EXCLUDED via {@link isPlainObject} -- a
 * plain data object carrying no keys.
 *
 * It lives HERE rather than in `utils/validation/type-guards.ts`, where it used to sit, for the reason that leaf's own
 * ban records: a pure `utils/` leaf may not value-import a `utils/` sibling, so `utils/data/null-if-empty.ts` had
 * re-declared the body privately and the two spellings answered to one name. Composing a const leaf is not a reason to
 * stay outside const/ -- {@link isPlainObject} is right here, so composing it costs nothing and the second spelling
 * disappears.
 */
export function isEmptyObject(value: unknown): boolean {
	return isPlainObject(value) && Object.keys(value).length === 0;
}

/**
 * CAN THIS VALUE CARRY A PROPERTY AT ALL -- an object or a function, and not `null`.
 *
 * The screen a caller runs BEFORE probing one member off an unknown, and it is load-bearing rather than defensive
 * noise: without it the probe is a property read on whatever arrived, and the optional-chained spelling a hand-roll
 * reaches for agrees only by accident. Deliberately WIDER than {@link isPlainObject}: a function and a class instance
 * both carry properties and both must pass, which is exactly why this is its own guard and not a call to that one.
 *
 * Two carriers, and neither could reach the other's copy: `isThenable` (`const/thenable-guard.ts`) screens before
 * reading `.then`, and `disposeRpcStub` (`const/rpc-stub-dispose.ts`) before reading its symbol-keyed disposer.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): both carriers named above are `const/` leaves.
 */
export function isPropertyBearing(value: unknown): boolean {
	return value !== null && (typeof value === 'object' || typeof value === 'function');
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): does an ALREADY-NARROWED object carry any own enumerable
 * string key -- the `Object.keys(x).length > 0` idiom, spelled once. Twenty-four sites across twenty-two files and
 * seven layers carried it verbatim.
 *
 * NOT `!isEmptyObject(value)`, and the difference is the leaf's one behavioural axis rather than a nuance. That
 * predicate takes `unknown` and EXCLUDES arrays, so `[]`, `null`, a string and a number all answer `false` to it and
 * therefore `true` to its negation -- while `Object.keys([]).length > 0` is `false` and `Object.keys(null)` THROWS.
 * Adopting the negation at these sites would have been byte-invisible and behaviour-changing at every one of them,
 * which is the exact failure this leaf's header records for the two opposite `asRecord` exports.
 *
 * THE PARAMETER IS `object`, NOT `unknown`, and that is the guard rather than a weakening: it is the same constraint
 * `Object.keys` itself imposes, so a site that typechecked at base typechecks here and a site holding an unnarrowed
 * `unknown` still cannot call this without narrowing first. That is what keeps the throwing `null` case unspellable
 * instead of silently coerced -- the miss-value convention this leaf's header names, applied to a predicate whose
 * honest answer for a non-object is "you may not ask".
 */
export function hasAnyKeys(value: object): boolean {
	// The FIRST own key answers, and no key array is built: a caller asking of a ~660-key map on each of 59,383 catalog
	// records paid 39 M array slots for a yes (2026-09-18). `for...in` plus `Object.hasOwn` is exactly `Object.keys`'s
	// set -- own, enumerable, string-keyed -- so the answer is unchanged for every input, arrays included.
	for (const key in value) if (Object.hasOwn(value, key)) return true;
	return false;
}

/** `record` itself when it carries a key, else null -- the "built nothing" collapse a sanitizer or a mapper answers
 *  after filling a fresh record, so an empty result is absent rather than `{}`.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): composes the same-file {@link hasAnyKeys}; homed on the const
 *  floor so the const-floor metadata sanitizer and the utils cleaner read one body. */
export function nullIfEmpty<T extends Record<string, unknown>>(record: T): T | null {
	return hasAnyKeys(record) ? record : null;
}

/** The keys of `entries` whose value is null, in entry order -- over a Map's entries or `Object.entries`, the "still
 *  open" keys a registry (a null migrations dir) and a reversal book (a leg no reversal answers) each read with a
 *  `filter(([, v]) => v === null).map(([k]) => k)` pair (2026-09-28 fold).
 *  @runtime-derived-ssot-helper (const-leaf-pinned): read by a rule leaf and a `utils/` ledger leaf. */
export function nullValuedKeys<K>(entries: Iterable<readonly [K, unknown]>): K[] {
	const keys: K[] = [];
	for (const [key, value] of entries) if (value === null) keys.push(key);
	return keys;
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): generic guard homed in const/ so pure utils/ leaves can
 * value-import it without a forbidden cross-utils sibling dependency. ARRAYS INCLUDED, narrowed to a string-keyed
 * record so the guarded branch can index by key without a cast. THE canonical array-including predicate: the sole
 * runtime body in this leaf, which the two coercers below delegate to. Deliberately distinct from
 * {@link isPlainObject}, the one real behavioural difference here: an array passes this and fails that.
 */
export function isRecordValue(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object' && value !== null;
}

/** {@link isRecordValue} AND at least one own key -- the `!isRecordValue(x) || Object.keys(x).length === 0` refusal a
 *  manifest cache and a reword door each spelled (2026-09-28 fold), composed on the same-file {@link hasAnyKeys}.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): read by a `const/` cache leaf and a `flows/` door. */
export function isNonEmptyRecord(value: unknown): value is Record<string, unknown> {
	return isRecordValue(value) && hasAnyKeys(value);
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): generic coercer homed in const/ so pure utils/ leaves can
 * value-import it without a forbidden cross-utils sibling dependency. COERCER, not a guard: returns the narrowed
 * record or `null`, so a call site can bind the result in one expression (`const row = asRecordOrNull(x)`) instead
 * of guarding then re-referencing. ARRAYS INCLUDED, matching {@link isRecordValue}'s predicate exactly. Use
 * {@link asRecordOrUndefined} where the miss value must chain through `??` against another optional.
 */
export function asRecordOrNull(value: unknown): Record<string, unknown> | null {
	return isRecordValue(value) ? value : null;
}

/** `value` as a record when its own `id` is `id`, else null -- the "matched by id, never by name" read a loaded
 *  module's exports and a landed record leaf both take.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): a coercer beside {@link asRecordOrNull}, since a `false` from a
 *  predicate would also cover a record of another id. */
export function recordOfId(value: unknown, id: string): Record<string, unknown> | null {
	return isRecordValue(value) && value.id === id ? value : null;
}

/** A rule's structured `metadata` as the optional member a violation row spreads: `{ metadata }` when it is a record
 *  ({@link isRecordValue}, arrays included), no member otherwise. The analyzer row and the report row both carry it
 *  through this one gate, so the two hops cannot disagree on what counts as metadata.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): a coercer beside {@link asRecordOrNull}. */
export function structuredMetadataMember(metadata: unknown): { metadata?: Record<string, unknown> } {
	return isRecordValue(metadata) ? { metadata } : {};
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): generic coercer homed in const/ so pure utils/ leaves can
 * value-import it without a forbidden cross-utils sibling dependency. COERCER, not a guard, and the ARRAYS-EXCLUDED
 * half of the coercer pair: `[]` misses as `null` here and PASSES {@link asRecordOrNull}. Matches
 * {@link isPlainObject}'s predicate exactly, so a call site wanting "a plain data object or nothing" binds in one
 * expression instead of guarding then re-referencing.
 *
 * It exists because this leaf published the array-excluding PREDICATE and the array-including COERCER but not this
 * combination, and two utils/ leaves hand-rolled it privately in consequence -- the gap one of them recorded in its
 * own docstring while re-declaring the body rather than closing it.
 */
export function asPlainObjectOrNull(value: unknown): Record<string, unknown> | null {
	return isPlainObject(value) ? value : null;
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the ARRAYS-EXCLUDED `key` member of an ARRAYS-EXCLUDED record, or
 * `null` when either level misses -- {@link asPlainObjectOrNull} at both levels, the two-level read a journal step row's
 * `output` opens with (`isPlainObject(step) && isPlainObject(step.output)`), bound in one expression.
 */
export function plainObjectMemberOrNull(value: unknown, key: string): Record<string, unknown> | null {
	return asPlainObjectOrNull(asPlainObjectOrNull(value)?.[key]);
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): generic coercer homed in const/ so pure utils/ leaves can
 * value-import it without a forbidden cross-utils sibling dependency. COERCER, not a guard, and the ARRAYS-EXCLUDED
 * half of the EMPTY-RECORD coercer pair: a non-object, `null` and an ARRAY all miss as a fresh `{}`. Matches
 * {@link isPlainObject}'s predicate exactly.
 *
 * TOTAL, not partial -- the return type carries no `| null` and no `| undefined`, which is the whole point: a caller
 * spreads it, indexes it or hands it to a `Record<string, unknown>` parameter with no second branch. Use
 * {@link asPlainObjectOrNull} where the MISS must stay distinguishable from an empty hit.
 *
 * THE MISS OBJECT IS FRESHLY ALLOCATED PER CALL rather than a shared frozen singleton, and that is deliberate: every
 * retired call site returned its own literal, so a shared instance would newly alias N unrelated call sites onto one
 * object that any of them may mutate or spread-assign into.
 */
export function asPlainObjectOrEmpty(value: unknown): Record<string, unknown> {
	return isPlainObject(value) ? value : {};
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): a PARSED JSON value read as a numeric map -- a plain object's
 * finite members through `finiteNumberMembers`, and an array, `null` or any scalar as `{}`. The call row's parsed signal
 * rollup (`flows/connect-call-detail.ts`, and the desk's `readSignalMap` off the same column) and the freshness-stamp
 * map (`utils/type-definitions/freshness-stamp-core.ts`) each spelled the conditional. ARRAYS EXCLUDED on purpose: `Object.entries` on `[3, 5]` would yield INDEX keys.
 */
export function finiteNumberMembersOrEmpty(value: unknown): Record<string, number> {
	return isPlainObject(value) ? finiteNumberMembers(value) : {};
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): generic coercer homed in const/ so pure utils/ leaves can
 * value-import it without a forbidden cross-utils sibling dependency. Identical predicate to
 * {@link asRecordOrNull} but misses as `undefined` -- the form envelope readers want, since `undefined` chains
 * through optional-property access and `??` fallbacks without a null-vs-undefined branch.
 */
export function asRecordOrUndefined(value: unknown): Record<string, unknown> | undefined {
	return isRecordValue(value) ? value : undefined;
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): generic coercer homed in const/ so pure utils/ leaves can
 * value-import it without a forbidden cross-utils sibling dependency. COERCER, not a guard, and the ARRAYS-EXCLUDED
 * half of the UNDEFINED-MISS coercer pair: `[]` misses as `undefined` here and PASSES {@link asRecordOrUndefined}
 * directly above. Matches {@link isPlainObject}'s predicate exactly.
 *
 * THE LAST EMPTY CELL of the grid this leaf's header describes -- two predicates by three miss values -- and it was
 * empty for the reason the {@link asPlainObjectOrNull} note records one axis over: the leaf published the
 * array-excluding PREDICATE and the undefined-missing COERCER but never their combination, so every call site wanting
 * both wrote the body out. Measured 2026-09-04 over the tracked `.ts`/`.tsx` tree, comment-stripped: 31 sites spell
 * `isPlainObject(x) ? x : undefined` with the SAME expression on both arms, across `flows/`, `tools/`, `spa/`,
 * `utils/`, `microservices/` and this leaf's own layer.
 *
 * NOT THE CURE FOR THE MEMBER-READ FORM, which the same census separates rather than lumps: 29 further sites spell
 * `isPlainObject(x) ? x.member : undefined`, whose value is the MEMBER and not the object. `asPlainObjectOrUndefined(x)?.member`
 * is the equivalent rewrite there, but it is a different edit with a different reviewable shape, so those sites are
 * not this export's queue and adopting it at them by pattern would be a sweep that cannot tell the two apart.
 *
 * Use {@link asPlainObjectOrNull} where the miss must be `null` (a distinguishable absence rather than one that chains
 * through `?.` and `??`), and {@link asPlainObjectOrEmpty} where the caller wants a total value it can spread.
 */
export function asPlainObjectOrUndefined(value: unknown): Record<string, unknown> | undefined {
	return isPlainObject(value) ? value : undefined;
}

/**
 * Whether a record carries exactly ONE member -- the shape a tagged wrapper, a sole-kind consumer and a bare plan each
 * test for, spelled once rather than as `Object.keys(value).length === 1` at every reader. A CARDINALITY read over a
 * record one of the two predicates above already narrowed, not a third predicate over a single value.
 * @runtime-derived-ssot-helper (const-leaf-pinned): held beside the narrowing it follows.
 */
export function isSingleMemberRecord(value: Readonly<Record<string, unknown>>): boolean {
	return Object.keys(value).length === 1;
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): generic coercer homed in const/ so pure utils/ leaves can
 * value-import it without a forbidden cross-utils sibling dependency. COERCER, not a guard, and the ARRAYS-INCLUDED
 * half of the EMPTY-RECORD coercer pair: identical predicate to {@link asRecordOrNull}, so an ARRAY passes THROUGH as
 * itself and only a non-object or `null` misses as a fresh `{}`.
 *
 * TOTAL, not partial -- the return type carries no `| null` and no `| undefined`, which is what a caller wants when the
 * value is about to be spread, indexed, or passed to a `Record<string, unknown>` parameter. Use
 * {@link asRecordOrUndefined} where the MISS must chain through `??`, and {@link asPlainObjectOrEmpty} where an array
 * must NOT reach the call site.
 *
 * THE MISS OBJECT IS FRESHLY ALLOCATED PER CALL; see {@link asPlainObjectOrEmpty} for why a shared singleton is the
 * wrong shape here.
 */
export function asRecordOrEmpty(value: unknown): Record<string, unknown> {
	return isRecordValue(value) ? value : {};
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the TYPE-PRESERVING twin of {@link asRecordOrEmpty}, for a
 * receiver whose DECLARED type is already narrower than `Record<string, unknown>` -- a persisted read typed through an
 * unchecked `storage.get<T>()` generic, a wire row typed by its schema. Same runtime body, same freshly-allocated
 * `{}` miss; the ONLY difference is that the hit branch comes back as the caller's own `T` instead of widened.
 *
 * IT EXISTS BECAUSE THE LEAF'S GRID WAS TWO PREDICATES BY THREE MISS VALUES OVER `unknown` INPUTS ONLY, and a TYPED
 * receiver had no cell at all. `asRecordOrEmpty(signals)` on a `Record<string, number>` field returns
 * `Record<string, unknown>`, so every spread member widens to `unknown` and the assignment back to the declared type
 * fails -- the site's only remaining move was to hand-roll `isRecordValue(x) ? x : {}`, which is exactly the shape
 * `QUAL-GUARD-COERCER-HALF-ADOPTION-001` convicts. A rule that names a cure a site cannot take is a false positive
 * with an SSOT-shaped root cause, so the missing cell is published here rather than excused at the matcher.
 *
 * THE RETURN IS A UNION AND NOT A BARE `T`, and that is soundness rather than caution: on a miss the value really is
 * `{}`, so a `T` carrying REQUIRED properties would be a lie -- `Record<string, never>` is assignable wherever an
 * index-signature `T` is expected (`never` widens to anything) while a caller whose `T` has required members is
 * forced to see the miss branch. It is the same type the hand-rolled ternary already inferred, which is what makes
 * the adoption byte-for-byte behaviour-preserving rather than a narrowing the caller must re-check.
 *
 * NOT HARVESTED BY `QUAL-GUARD-COERCER-HALF-ADOPTION-001`'s derivation, deliberately: that parse requires the literal
 * `(value: unknown)` parameter, so this member publishes NO new site arm and no line acquires a second prescription.
 * The rule's `fix` names it in prose for the typed-receiver case, which is where the choice between the two belongs.
 */
export function asDeclaredRecordOrEmpty<T extends Record<string, unknown>>(value: T): T | Record<string, never> {
	return isRecordValue(value) ? value : {};
}

/**
 * The DIAGNOSTIC key list of an unknown value: comma-joined own keys, or the literal `(none)` when there are none --
 * which is what distinguishes "the producer sent an empty object" from "the producer sent nothing at all" in a failure
 * reason. For MESSAGES only; nothing branches on it.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): coalesces through the same-file {@link asRecordOrEmpty}, so a
 * non-record degrades to the same empty answer every other reader here gives it.
 */
export function recordKeyList(value: unknown): string {
	return Object.keys(asRecordOrEmpty(value)).join(',') || '(none)';
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): generic row-array coalescer homed in const/ so pure utils/
 * leaves can value-import it without a forbidden cross-utils sibling dependency. A flow-template-resolved rows
 * value is null when the node never ran or failed; anything non-array degrades to [].
 */
export function rowsOrEmpty(value: unknown): Record<string, unknown>[] {
	return Array.isArray(value) ? (value as Record<string, unknown>[]) : [];
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): generic string-field reader over an UNKNOWN value, homed in const/
 * so pure utils/ leaves can value-import it without a forbidden cross-utils sibling dependency. THE one body for the
 * default-substituting read: a non-record input, a missing key and a non-string value all yield `defaultValue`.
 *
 * The sole runtime body behind two utils spellings that each carried their own copy -- `extractStringInput`
 * (`utils/validation/extract-string-field.ts`) and `readString` (`utils/messaging/message-display-info.ts`). Both
 * declare a parameter DEFAULT, so each is a distinct contract rather than an alias and they stay as named delegations.
 * The third spelling, `extractStringOr`, forwarded VERBATIM and was drained onto this body 2026-08-10 under
 * `QUAL-CONST-DELEGATION-WRAPPER-001`; its callers import this name directly. Distinct from
 * {@link rowString}, whose parameter is an ALREADY-narrowed record and whose miss value is `null`, not a default.
 */
export function recordStringOr(value: unknown, key: string, defaultValue: string): string {
	const field = recordMember(value, key);
	return typeof field === 'string' ? field : defaultValue;
}

/** The `user_id` a row carries, or '' where it carries none: what every roster read maps its rows over (the group
 *  join request, the voice escalation dispatch; 2026-09-28 fold). @runtime-derived-ssot-helper (const-leaf-pinned). */
export function recordUserIdOrEmpty(value: unknown): string {
	return recordStringOr(value, DB_COLUMNS.USER_ID, '');
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): ONE MEMBER of an UNKNOWN value, unread and untyped -- the
 * `isRecordValue(value) ? value[key] : undefined` step every keyed reader in this family starts with, and which census
 * batch readers, a telephony list read and the Stripe metadata read each spelled inline. A non-record input answers
 * `undefined`, never a throw. The typed readers ({@link recordStringOr} and siblings) decide what the member must be.
 */
export function recordMember(value: unknown, key: string): unknown {
	return isRecordValue(value) ? value[key] : undefined;
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): generic FINITE-number field reader over an UNKNOWN value, homed in
 * const/ for the same reason as {@link recordStringOr} -- `utils/flow-engine/meta-changes.ts` and
 * `utils/flows/flow-body-rows.ts` are pure utils leaves that may not import a utils sibling, so a numeric extractor
 * parked in `utils/data/record-fields.ts` was unreachable from them and both hand-rolled the dance instead. THE one
 * body for the defaulted numeric read: a non-record input, a missing key, a non-number value and a NON-FINITE number
 * (`NaN` / +-Infinity) all yield `defaultValue`.
 */
export function recordFiniteNumberOr(value: unknown, key: string, defaultValue: number): number {
	const field = recordMember(value, key);
	return isFiniteNumber(field) ? field : defaultValue;
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): generic boolean-field reader over an UNKNOWN value, homed in
 * const/ alongside {@link recordFiniteNumberOr} so the number and boolean arms of one extraction family share a
 * layer. A non-record input, a missing key and a non-boolean value all yield `defaultValue`.
 */
export function recordBooleanOr(value: unknown, key: string, defaultValue: boolean): boolean {
	const field = recordMember(value, key);
	return typeof field === 'boolean' ? field : defaultValue;
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): generic COLUMN-KIND check over an already-narrowed record, homed
 * in const/ so pure utils/ leaves can value-import it without a forbidden cross-utils sibling dependency.
 *
 * THE ROW-DRIFT half of a D1 row guard, and the reason it is a check rather than a reader: {@link recordStringOr} and
 * its siblings COERCE a wrong-kinded column to a default, which is exactly the silent substitution a money path must
 * not make. This one REFUSES the row instead, so the caller's fail-closed arm runs.
 *
 * ABSENT AND NULL BOTH PASS, deliberately: a D1 row's nullable column arrives as `null` on a healthy read, and an
 * `undefined` is the same absence one hop later through a flow template. What the check exists to catch is a column
 * that arrived carrying a value of the WRONG kind -- a drifted `TEXT` in a column the row type declares `number`.
 */
export function recordKeysAreStringOrNull(record: Record<string, unknown>, keys: readonly string[]): boolean {
	return keys.every((key) => record[key] === null || record[key] === undefined || typeof record[key] === 'string');
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the FINITE-NUMBER arm of {@link recordKeysAreStringOrNull}, a
 * separate body for the same reason {@link recordFiniteNumberOr} is separate from {@link recordStringOr} -- `NaN` and
 * the infinities are `typeof 'number'` and must NOT pass. On a money column that distinction is the whole point: a
 * `NaN` amount flows through `Number(x) || 0` as a silent ZERO, which passes every limit check there is.
 */
export function recordKeysAreFiniteNumberOrNull(record: Record<string, unknown>, keys: readonly string[]): boolean {
	return keys.every(
		(key) => record[key] === null || record[key] === undefined || (typeof record[key] === 'number' && Number.isFinite(record[key])),
	);
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): generic string-field reader over a nullable record, homed in
 * const/ so pure utils/ leaves can value-import it without a forbidden cross-utils sibling dependency. Null for a
 * missing record, a missing key, or a non-string value.
 */
export function rowString(rec: Record<string, unknown> | null | undefined, key: string): string | null {
	if (rec === null || rec === undefined) return null;
	const value = rec[key];
	return stringOrNull(value);
}
